import {
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid2,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import {
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useEffect,
} from "react";
import { useGetPropertyTypesQuery } from "../../propertyTypes/propertyTypesApiSlice";
import { useGetAmenitiesQuery } from "../../amenities/api/amenitiesApiSlice";
import { useGetHouseRulesQuery } from "../../houseRules/api/houseRulesApiSlice";

// // Standardized Lists for PG Aggregators
// const AMENITIES_LIST = [
//   { value: "WiFi", label: "WiFi" },
//   { value: "AC", label: "Air Conditioning" },
//   { value: "Power Backup", label: "Power Backup" },
//   { value: "RO Water", label: "RO Water" },
//   { value: "Laundry", label: "Laundry Service" },
//   { value: "CCTV", label: "CCTV Security" },
//   { value: "Parking (Bike)", label: "Bike Parking" },
//   { value: "Parking (Car)", label: "Car Parking" },
//   { value: "Lift", label: "Elevator/Lift" },
//   { value: "Gym", label: "Gym Facility" },
//   { value: "TV in Common Area", label: "Common TV" },
//   { value: "Fridge", label: "Refrigerator" },
// ];

// const HOUSE_RULES_LIST = [
//   { value: "No Smoking", label: "No Smoking" },
//   { value: "No Alcohol", label: "No Alcohol" },
//   { value: "No Non-Veg", label: "Vegetarian Only" },
//   { value: "No Opposite Gender Guests", label: "No Opposite Gender Guests" },
//   { value: "Gate Closing Time (10 PM)", label: "Gate Closes at 10 PM" },
//   { value: "Visitors Allowed (Day Only)", label: "Day Visitors Only" },
// ];

const PropertyForm = forwardRef(({ onDataChange, initialData }, ref) => {
  // 1. Fetch Master Data
  const {
    data: propertyTypes,
    isLoading: loadingPropertyTypes,
    isSuccess,
  } = useGetPropertyTypesQuery();

  const { data: amenitiesData, isLoading: loadingAmenities } =
    useGetAmenitiesQuery();

  const { data: rulesData, isLoading: loadingRules } = useGetHouseRulesQuery();

  const maxLength = 100;

  // 2. Memoized Filtering: Only show 'property' or 'both' categories in this form
  const buildingAmenities = useMemo(() => {
    if (!amenitiesData) return [];
    // Handle RTK Query entity pattern if you use it, else assume array
    const list = amenitiesData.ids
      ? amenitiesData.ids.map((id) => amenitiesData.entities[id])
      : amenitiesData;
    return list.filter(
      (a) => a.category === "property" || a.category === "both",
    );
  }, [amenitiesData]);

  const houseRules = useMemo(() => {
    if (!rulesData) return [];
    return rulesData.ids
      ? rulesData.ids.map((id) => rulesData.entities[id])
      : rulesData;
  }, [rulesData]);

  // Refs
  const titleRef = useRef(null);
  const propertyTypeRef = useRef(null);
  const managerNameRef = useRef(null);
  const managerPhoneRef = useRef(null);
  const genderMaleRef = useRef(null);
  const tenantTypeRef = useRef(null);

  // Error States
  const [errors, setErrors] = useState({
    title: "",
    propertyType: "",
    managerName: "",
    managerPhone: "",
    gender: "",
    tenantType: "",
  });

  // Form State
  const [formData, setFormData] = useState({
    title: initialData?.title || "",
    propertyType: initialData?.propertyType || "",
    gender: initialData?.gender || "",
    tenantType: initialData?.tenantType || "",
    managerName: initialData?.managerName || "",
    managerPhone: initialData?.managerPhone || "",
    // amenities: initialData?.amenities || [], // Array of ObjectIds
    amenities: (initialData.amenities || []).map((amenity) =>
      typeof amenity === "object" ? amenity.id || amenity._id : amenity,
    ),
    // rules: initialData?.rules || [], // Array of ObjectIds
    rules: (initialData?.rules || []).map((rule) =>
      typeof rule === "object" ? rule._id || rule.id : rule,
    ),
  });

  // Add useEffect to the imports from 'react'
  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title || "",
        propertyType: initialData.propertyType || "",
        gender: initialData.gender || "",
        tenantType: initialData.tenantType || "",
        managerName: initialData.managerName || "",
        managerPhone: initialData.managerPhone || "",
        // amenities: initialData?.amenities || [], // Array of ObjectIds
        amenities: (initialData.amenities || []).map((amenity) =>
          typeof amenity === "object" ? amenity.id || amenity._id : amenity,
        ),
        // rules: initialData?.rules || [], // Array of ObjectIds
        rules: (initialData?.rules || []).map((rule) =>
          typeof rule === "object" ? rule._id || rule.id : rule,
        ),
      });
    }
  }, [initialData]); // This triggers whenever initialData changes

  // Handle Text/Select/Radio Changes
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Clear specific error on change
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }

    const updatedData = { ...formData, [name]: value };
    setFormData(updatedData);
    onDataChange(updatedData);
  };

  // Handle Checkbox Changes (Amenities & Rules)
  const handleCheckboxChange = (category, id) => {
    setFormData((prev) => {
      const currentList = prev[category];
      const newList = currentList.includes(id)
        ? currentList.filter((item) => item !== id)
        : [...currentList, id];

      const updatedData = { ...prev, [category]: newList };
      onDataChange(updatedData);
      return updatedData;
    });
  };

  // Validation Logic
  const validateUserInput = () => {
    let isValid = true;
    const newErrors = { ...errors };
    let fieldToFocus = null;

    // Reset all errors first
    Object.keys(newErrors).forEach((key) => {
      newErrors[key] = "";
    });

    // 1. Title
    if (formData.title.trim() === "") {
      newErrors.title = "PG Name is required";
      isValid = false;
      if (!fieldToFocus) fieldToFocus = titleRef;
    } else if (formData.title.length > maxLength) {
      newErrors.title = `Max ${maxLength} characters`;
      isValid = false;
      if (!fieldToFocus) fieldToFocus = titleRef;
    }

    // if (formData.title.trim() === "") {
    //   setTitleError(true);
    //   setTitleErrorMsg("This field is required");
    //   titleRef.current.focus();
    //   return false;
    // } else if (formData.title.length > maxLength) {
    //   setTitleError(true);
    //   setTitleErrorMsg(
    //     `This field cannot be more than ${maxLength} characters`
    //   );
    //   titleRef.current.focus();
    //   return false;
    // } else {
    //   setTitleError(false);
    //   setTitleErrorMsg("");
    // }

    // 2. Property Type
    if (!formData.propertyType) {
      newErrors.propertyType = "Select a property type";
      isValid = false;
      if (!fieldToFocus) fieldToFocus = propertyTypeRef;
    }

    // 3. Gender
    if (!formData.gender) {
      newErrors.gender = "Gender restriction is required";
      isValid = false;
      if (!fieldToFocus) fieldToFocus = genderMaleRef;
    }

    // 4. Tenant Type
    if (!formData.tenantType) {
      newErrors.tenantType = "Tenant Type is required";
      isValid = false;
      if (!fieldToFocus) fieldToFocus = tenantTypeRef;
    }

    // 5. Manager Details
    if (!formData.managerName.trim()) {
      newErrors.managerName = "Manager Name is required";
      isValid = false;
      if (!fieldToFocus) fieldToFocus = managerNameRef;
    }

    const phoneRegex = /^[0-9]{10}$/;

    if (!formData.managerPhone.trim()) {
      newErrors.managerPhone = "Manager Phone is required";
      isValid = false;
      if (!fieldToFocus) fieldToFocus = managerPhoneRef;
    } else if (!formData.managerPhone.match(phoneRegex)) {
      newErrors.managerPhone = "Enter valid 10-digit mobile number";
      isValid = false;
      if (!fieldToFocus) fieldToFocus = managerPhoneRef;
    }

    setErrors(newErrors);

    // // Scroll to the first error
    // if (!isValid) {
    //   setTimeout(() => {
    //     const firstErrorField = document.querySelector(".Mui-error");
    //     if (firstErrorField) {
    //       firstErrorField.scrollIntoView({
    //         behavior: "smooth",
    //         block: "center",
    //       });
    //     }
    //   }, 100);
    // }

    // Focus on the first invalid field
    if (fieldToFocus && fieldToFocus.current) {
      // For radio buttons, we need to use a different approach
      if (fieldToFocus === genderMaleRef) {
        // For radio button group, we need to focus on the first radio button
        setTimeout(() => {
          const radioInput = document.querySelector('input[value="male"]');
          if (radioInput) {
            radioInput.focus();
          }

          // Also scroll to the gender section
          const genderSection = document.querySelector(
            '[data-section="tenant"]',
          );
          if (genderSection) {
            genderSection.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }, 100);
      } else if (fieldToFocus === tenantTypeRef) {
        // For radio button group, we need to focus on the first radio button
        setTimeout(() => {
          const radioInput = document.querySelector('input[value="students"]');
          if (radioInput) {
            radioInput.focus();
          }

          // Also scroll to the tenantType section
          const tenantTypeSection = document.querySelector(
            '[data-section="tenant"]',
          );
          if (tenantTypeSection) {
            tenantTypeSection.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }, 100);
      } else {
        // For other fields, use the ref directly
        fieldToFocus.current.focus();
        setTimeout(() => {
          const firstErrorField = document.querySelector(".Mui-error");
          firstErrorField?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }, 100);
      }
    }

    return isValid;
  };

  useImperativeHandle(ref, () => ({
    validateUserInput,
    getFormData: () => ({ ...formData }),
  }));

  if (loadingPropertyTypes || loadingAmenities || loadingRules) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 800,
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      {/* Basic Information Section */}
      <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
          Basic Information
        </Typography>
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="PG Name"
              name="title"
              inputRef={titleRef}
              value={formData.title}
              onChange={handleChange}
              required
              error={!!errors.title}
              helperText={errors.title}
              placeholder="e.g. Sunrise Gents PG"
              slotProps={{
                htmlInput: {
                  maxLength: maxLength,
                },
              }}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth error={!!errors.propertyType} required>
              <InputLabel>Property Type</InputLabel>
              <Select
                name="propertyType"
                value={formData.propertyType}
                onChange={handleChange}
                inputRef={propertyTypeRef}
                label="Property Type"
                endAdornment={
                  loadingPropertyTypes && (
                    <InputAdornment position="end" sx={{ mr: 2 }}>
                      <CircularProgress size={20} />
                    </InputAdornment>
                  )
                }
              >
                <MenuItem value="">-- Select --</MenuItem>
                {isSuccess &&
                  Object.values(propertyTypes.entities)
                    // .filter((pt) => pt.name !== "Hotel")
                    .map((pt) => (
                      <MenuItem key={pt.id} value={pt.id}>
                        {pt.name}
                      </MenuItem>
                    ))}
              </Select>
              <FormHelperText>{errors.propertyType}</FormHelperText>
            </FormControl>
          </Grid2>
        </Grid2>
      </Paper>

      {/* Target Audience Section */}
      <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }} data-section="tenant">
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
          {/* Intended Residents */}
          Who is this PG for?
        </Typography>
        <Box sx={{ mb: 1 }}>
          <FormLabel
            component="legend"
            required
            sx={{
              display: "block",
              color: errors.gender ? "error.main" : "text.primary",
            }}
          >
            Gender Preference
          </FormLabel>
          <RadioGroup
            name="gender"
            value={formData.gender}
            onChange={handleChange}
            sx={{ flexWrap: "wrap" }}
          >
            <Grid2 container spacing={1}>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControlLabel
                  value="male"
                  control={<Radio inputRef={genderMaleRef} />}
                  label="Gents Only"
                  sx={{ mr: 3 }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControlLabel
                  value="female"
                  control={<Radio />}
                  label="Ladies Only"
                  sx={{ mr: 3 }}
                />
              </Grid2>
              {/* <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControlLabel
                  value="coed"
                  control={<Radio />}
                  label="Co-Ed (Separate Floors)"
                />
              </Grid2> */}
            </Grid2>
          </RadioGroup>
          {errors.gender && (
            <Typography
              color="error"
              variant="caption"
              sx={{ display: "block" }}
            >
              {errors.gender}
            </Typography>
          )}
        </Box>

        <Divider />

        <Box sx={{ mt: 3 }}>
          <FormLabel
            component="legend"
            sx={{
              display: "block",
              color: errors.tenantType ? "error.main" : "text.primary",
            }}
            required
          >
            Preferred Tenant Type
          </FormLabel>
          <RadioGroup
            // row
            name="tenantType"
            value={formData.tenantType}
            onChange={handleChange}
            sx={{ flexWrap: "wrap" }}
          >
            <Grid2 container spacing={1}>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControlLabel
                  value="students"
                  // control={<Radio />}
                  control={<Radio inputRef={tenantTypeRef} />}
                  label="Students"
                  sx={{ mr: 3 }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControlLabel
                  value="professionals"
                  control={<Radio />}
                  label="Professionals"
                  sx={{ mr: 3 }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControlLabel
                  value="both"
                  control={<Radio />}
                  label="Both"
                />
              </Grid2>
            </Grid2>
          </RadioGroup>
          {errors.tenantType && (
            <Typography
              color="error"
              variant="caption"
              sx={{ display: "block" }}
            >
              {errors.tenantType}
            </Typography>
          )}
        </Box>
      </Paper>

      {/* Manager Details Section */}
      <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
          Manager Details
        </Typography>
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Manager Name"
              name="managerName"
              inputRef={managerNameRef}
              value={formData.managerName}
              onChange={handleChange}
              error={!!errors.managerName}
              helperText={errors.managerName}
              required
              placeholder="Enter full name"
              slotProps={{
                htmlInput: {
                  maxLength: maxLength, // Hard stop
                },
              }}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Manager Phone"
              name="managerPhone"
              inputRef={managerPhoneRef}
              value={formData.managerPhone}
              onChange={handleChange}
              error={!!errors.managerPhone}
              helperText={errors.managerPhone}
              required
              type="tel"
              placeholder="10-digit mobile number"
              slotProps={{
                htmlInput: {
                  maxLength: 10,
                },
              }}
            />
          </Grid2>
        </Grid2>
      </Paper>

      {/* Dynamic Amenities Section */}
      <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Building Amenities
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          gutterBottom
          sx={{ mb: 1 }}
        >
          Select all amenities available in your PG
        </Typography>
        <Grid2 container spacing={1}>
          {buildingAmenities.map((amenity) => (
            <Grid2
              size={{ xs: 6, sm: 4, md: 3 }}
              key={amenity.id || amenity._id}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.amenities.includes(
                      amenity.id || amenity._id,
                    )}
                    onChange={() =>
                      handleCheckboxChange(
                        "amenities",
                        amenity.id || amenity._id,
                      )
                    }
                    size="small"
                  />
                }
                label={<Typography variant="body2">{amenity.name}</Typography>}
                sx={{ mr: 0 }}
              />
            </Grid2>
          ))}
        </Grid2>
      </Paper>

      {/* Dynamic House Rules Section */}
      <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          House Rules
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          gutterBottom
          sx={{ mb: 1 }}
        >
          Set clear rules for your tenants
        </Typography>
        <Grid2 container spacing={1}>
          {houseRules.map((rule) => (
            <Grid2 size={{ xs: 12, sm: 6 }} key={rule.id || rule._id}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.rules.includes(rule.id || rule._id)}
                    onChange={() =>
                      handleCheckboxChange("rules", rule.id || rule._id)
                    }
                    size="small"
                    // color="secondary"
                  />
                }
                label={<Typography variant="body2">{rule.ruleText}</Typography>}
              />
            </Grid2>
          ))}
        </Grid2>
      </Paper>
    </Box>
  );
});

export default PropertyForm;
